/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useMemo } from 'react';
import { View } from '@az/starc-ui';
import type { YextHours, YextHolidayHour } from '../../interface/yext-types';
import styles from './StoreHours.module.scss';
import { DAYS_OF_WEEK } from '../../constants/daysOfWeek';
import { formatStoreTime } from '../../constants/formatStoreTime';
import { type StoreDayInfo } from '../../interface/types';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { Text } from '@az/starc-ui';
import { type Locale } from '@/types/i18n';
import { getHolidayHoursForWeek } from '../../utils/getHolidayHoursForWeek';
import starcClassOverrides from '@/theme/starcClassOverrides.module.scss';
import cx from 'classnames';

const labelMap = {
  storeHoursHeading: 'label_locations_storeHoursHeading',
  monday: 'label_locations_day_monday',
  tuesday: 'label_locations_day_tuesday',
  wednesday: 'label_locations_day_wednesday',
  thursday: 'label_locations_day_thursday',
  friday: 'label_locations_day_friday',
  saturday: 'label_locations_day_saturday',
  sunday: 'label_locations_day_sunday',
  holidayHours: 'label_locations_holidayHours',
  closed: 'label_locations_storeStatus_closed',
};

type StoreHoursProps = {
  hours: YextHours;
  today: StoreDayInfo | null;
  holidayHours?: YextHolidayHour[];
  storeTimezone?: string;
};

type DayRowProps = {
  dayKey: string;
  isToday: boolean;
  hasHoliday: boolean;
  isClosed: boolean;
  startTime?: string;
  endTime?: string;
  isHolidayTime?: boolean;
  labels: Record<string, string>;
  locale: Locale;
};

function DayRow({
  dayKey,
  isToday,
  hasHoliday,
  isClosed,
  startTime,
  endTime,
  isHolidayTime,
  labels,
  locale,
}: DayRowProps) {
  const textWeight = isToday ? 'bold' : 'regular';
  const timeSpanClass = isHolidayTime ? styles.holidayTime : undefined;

  return (
    <tr className={isToday ? styles.todayRow : undefined}>
      <td>
        <Text
          variant="small-body"
          className={starcClassOverrides['starc-small-body']}
          weight={textWeight}
        >
          {labels[dayKey]}
        </Text>
        {hasHoliday && (
          <Text
            variant="small-body"
            className={cx(styles.holidayLabel, starcClassOverrides['starc-small-body'])}
          >
            ({labels.holidayHours})
          </Text>
        )}
      </td>
      {isClosed ? (
        <td className={styles.timeCell} colSpan={2}>
          <Text
            variant="small-body"
            className={starcClassOverrides['starc-small-body']}
            weight={textWeight}
          >
            <span className={styles.closedText}>{labels.closed}</span>
          </Text>
        </td>
      ) : (
        <>
          <td className={styles.timeCell}>
            {startTime ? (
              <Text
                variant="small-body"
                className={starcClassOverrides['starc-small-body']}
                weight={textWeight}
              >
                <span className="sr-only">From </span>
                <span className={timeSpanClass}>{formatStoreTime(startTime, locale)}</span>
                <span aria-hidden> - </span>
              </Text>
            ) : null}
          </td>
          <td className={styles.timeCell}>
            {endTime ? (
              <Text
                variant="small-body"
                className={starcClassOverrides['starc-small-body']}
                weight={textWeight}
              >
                <span className="sr-only">To </span>
                <span className={timeSpanClass}>{formatStoreTime(endTime, locale)}</span>
              </Text>
            ) : null}
          </td>
        </>
      )}
    </tr>
  );
}

export function StoreHours({ hours, today, holidayHours, storeTimezone }: StoreHoursProps) {
  const labels = useLabels(labelMap);
  const locale = useLocale();

  const holidayMap = useMemo(
    () => getHolidayHoursForWeek(holidayHours, storeTimezone),
    [holidayHours, storeTimezone],
  );

  return (
    <View padding={4} direction="row" borderColor="disabled">
      <View width="100%">
        <table className={styles.hoursTable}>
          <tbody>
            {DAYS_OF_WEEK.map(({ key }) => {
              const dayHours = hours[key];
              const isToday = today?.day === key;
              const holiday = holidayMap[key];
              const hasHoliday = Boolean(holiday && !holiday.isRegularHours);

              let isClosed = dayHours?.isClosed ?? false;
              let startTime: string | undefined;
              let endTime: string | undefined;
              let isHolidayTime = false;

              if (hasHoliday && holiday?.isClosed) {
                isClosed = true;
                isHolidayTime = true;
              } else if (hasHoliday && holiday?.openIntervals?.length) {
                const holidayInterval = holiday.openIntervals[0];
                startTime = holidayInterval.start;
                endTime = holidayInterval.end;
                isHolidayTime = true;
              } else if (!isClosed) {
                const interval = dayHours?.openIntervals?.[0];
                startTime = interval?.start;
                endTime = interval?.end;
              }

              return (
                <DayRow
                  key={key}
                  dayKey={key}
                  isToday={isToday}
                  hasHoliday={hasHoliday}
                  isClosed={isClosed}
                  startTime={startTime}
                  endTime={endTime}
                  isHolidayTime={isHolidayTime}
                  labels={labels}
                  locale={locale}
                />
              );
            })}
          </tbody>
        </table>
      </View>
    </View>
  );
}
